import { useMemo, useState } from "react";
import { GrammarTags } from "@/components/ui/GrammarTags";
import { LanguageFlag } from "@/components/ui/LanguageFlag";
import { SourceBadge, SourceBadgeList } from "@/components/ui/SourceBadge";
import { Skeleton } from "@/components/ui/Skeleton";
import { focusRing } from "@/components/ui/buttonStyles";
import { NounParadigm } from "@/components/word/NounParadigm";
import { VerbParadigm } from "@/components/word/VerbParadigm";
import { WiktionaryCredit } from "@/components/word/WiktionaryCredit";
import {
  getFormTags,
  groupVerbForms,
  isBasicVerbGroup,
  uniqueTranslations,
} from "@/utils/grammar";
import { senseSources } from "@/utils/provenance";
import type { Word, WordFormInfo, WordSense } from "@/types/word";
import type { Language } from "@/types/language";
import { cn } from "@/utils/cn";

/** Shared id so a hosting dialog can label itself with the lemma heading. */
export const WORD_DETAIL_HEADING_ID = "word-detail-heading";

interface WordDetailProps {
  word: Word;
  /** Renders the flag beside the lemma. Omitted where the language is already implied. */
  language?: Language | null;
  /** 1 on the detail page, 2 inside a dialog that sits within another page. */
  headingLevel?: 1 | 2;
}

/** Mirrors the hero, gloss and three sense lines while the word loads. */
export function WordDetailSkeleton() {
  return (
    <div data-testid="word-skeleton" aria-hidden="true" className="space-y-8">
      <div className="space-y-3">
        <Skeleton className="h-12 w-2/3 sm:w-1/2" />
        <Skeleton line className="h-4 w-24" />
      </div>
      <Skeleton className="h-8 w-1/2" />
      <div className="space-y-3">
        <Skeleton line className="h-4 w-full" />
        <Skeleton line className="h-4 w-11/12" />
        <Skeleton line className="h-4 w-3/4" />
      </div>
    </div>
  );
}

const DECLINABLE_POS = new Set(["Noun", "Verb", "Adjective", "Pronoun"]);

/**
 * The word detail body — hero, gloss, senses and paradigm tables.
 * Shared by the word detail page and the flashcard detail modal.
 */
export function WordDetail({ word, language, headingLevel = 1 }: WordDetailProps) {
  const Heading = headingLevel === 2 ? "h2" : "h1";
  const isLemmaForm = word.form
    ? word.form.surface.toLowerCase() === word.lemma.toLowerCase()
    : true;
  const formTags = getFormTags(word);
  // A source belongs in the hero only when it speaks for the whole word: at
  // least two senses, all from that one source. With a single sense — or a mix —
  // the per-sense badges below already say it.
  const sources = senseSources(word.senses);
  const headerSources = word.senses.length > 1 && sources.length === 1 ? sources : [];
  const gloss = uniqueTranslations(word.senses);
  const hasSenses = word.senses.length > 0;
  const hasParadigm = word.forms.length > 0 && DECLINABLE_POS.has(word.part_of_speech);

  return (
    <div className="@container">
      <header className="animate-enter animate-enter-d1">
        <p className="font-mono text-xs uppercase tracking-widest text-muted">
          {word.part_of_speech}
          {word.grammatical_info.gender && (
            <>
              <span aria-hidden="true" className="mx-2 text-muted/60">·</span>
              {word.grammatical_info.gender}
            </>
          )}
        </p>
        <Heading
          id={WORD_DETAIL_HEADING_ID}
          className="mt-2 flex items-center gap-3 font-serif text-4xl sm:text-5xl text-heading leading-tight"
        >
          {language && <LanguageFlag isoCode={language.iso_code} size="l" />}
          {word.lemma}
        </Heading>
        {gloss.length > 0 && (
          <p className="mt-3 font-serif text-xl sm:text-2xl text-body leading-snug">
            {gloss.join(", ")}
          </p>
        )}
        <SourceBadgeList sources={headerSources} />
        {formTags.length > 0 && !isLemmaForm && (
          <div className="mt-4">
            <GrammarTags tags={formTags} size="md" />
          </div>
        )}
      </header>

      {(hasSenses || hasParadigm) && (
        <div
          className={cn(
            "mt-12 grid gap-12",
            hasSenses && hasParadigm && "@3xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] @3xl:gap-16",
          )}
        >
          {hasSenses && (
            <div className="animate-enter animate-enter-d2 min-w-0">
              <Senses senses={word.senses} lemma={word.lemma} />
            </div>
          )}
          {hasParadigm && (
            <div className="animate-enter animate-enter-d3 min-w-0 @container">
              <Paradigm forms={word.forms} pos={word.part_of_speech} iso={word.language_iso_code} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Senses ───────────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-mono text-xs uppercase tracking-widest text-muted">
      {children}
    </h3>
  );
}

function Senses({ senses, lemma }: { senses: WordSense[]; lemma: string }) {
  return (
    <section>
      <SectionLabel>Senses</SectionLabel>
      <ol className="mt-3">
        {senses.map((sense) => <SenseRow key={sense.id} sense={sense} />)}
      </ol>
      <WiktionaryCredit lemma={lemma} senses={senses} className="border-t border-line-soft pt-4" />
    </section>
  );
}

function SenseRow({ sense }: { sense: WordSense }) {
  const glosses = uniqueTranslations([sense], Infinity);
  const translations = glosses.join(", ");
  // Wiktionary often files a gloss as the definition too; a second copy of
  // "to have" under "have" says nothing new.
  const definition = sense.definition && !restatesGloss(sense.definition, glosses) ? sense.definition : null;

  return (
    <li className="grid grid-cols-[1.75rem_minmax(0,1fr)] border-t border-line-soft py-4">
      <span className="font-mono text-sm text-ordinal pt-0.5 select-none" aria-hidden="true">
        {sense.sense_index + 1}
      </span>
      <div className="min-w-0">
        {translations && (
          <p className="font-serif text-lg text-heading leading-relaxed">{translations}</p>
        )}
        {definition && (
          <p className="mt-1 text-base text-muted leading-relaxed">{definition}</p>
        )}
        <span className="block mt-2 empty:hidden">
          <SourceBadge source={sense.source} />
        </span>
      </div>
    </li>
  );
}

/** True when every phrase in the definition is already one of the glosses. */
function restatesGloss(definition: string, glosses: string[]): boolean {
  const norm = (s: string) => s.trim().toLowerCase().replace(/^to\s+/, "").replace(/\.$/, "");
  const known = new Set(glosses.map(norm));
  return definition.split(/[,;]/).every((part) => known.has(norm(part)));
}

// ── Paradigm ─────────────────────────────────────────────────────────────────

function Paradigm({ forms, pos, iso }: { forms: WordFormInfo[]; pos: string; iso?: string }) {
  if (pos === "Verb") return <Conjugation forms={forms} iso={iso} />;
  return <Declension forms={forms} pos={pos} />;
}

function Conjugation({ forms, iso }: { forms: WordFormInfo[]; iso?: string }) {
  const [showAll, setShowAll] = useState(false);
  const groups = useMemo(() => groupVerbForms(forms), [forms]);
  const basic = useMemo(() => groups.filter(isBasicVerbGroup), [groups]);
  const visible = showAll ? groups : basic;

  return (
    <section>
      <ParadigmHeader
        title="Conjugation"
        total={forms.length}
        showAll={showAll}
        canToggle={basic.length < groups.length}
        onToggle={() => setShowAll((v) => !v)}
      />
      <div className="mt-5">
        <VerbParadigm groups={visible} iso={iso} />
      </div>
    </section>
  );
}

function isBasicAdjective(f: WordFormInfo): boolean {
  return f.grammatical_info.degree === "positive_strong";
}

function Declension({ forms, pos }: { forms: WordFormInfo[]; pos: string }) {
  const [showAll, setShowAll] = useState(false);
  const basic = useMemo(
    () => (pos === "Adjective" ? forms.filter(isBasicAdjective) : forms),
    [forms, pos],
  );
  const visible = showAll || basic.length === 0 ? forms : basic;

  return (
    <section>
      <ParadigmHeader
        title="Declension"
        total={forms.length}
        showAll={showAll}
        canToggle={basic.length > 0 && basic.length < forms.length}
        onToggle={() => setShowAll((v) => !v)}
      />
      <div className="mt-5">
        <NounParadigm forms={visible} />
      </div>
    </section>
  );
}

interface ParadigmHeaderProps {
  title: string;
  total: number;
  showAll: boolean;
  canToggle: boolean;
  onToggle: () => void;
}

function ParadigmHeader({ title, total, showAll, canToggle, onToggle }: ParadigmHeaderProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <SectionLabel>{title}</SectionLabel>
      {canToggle && (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={showAll}
          className={cn(
            "-my-3 min-h-11 rounded px-2 text-sm text-muted transition-colors duration-200 hover:text-heading",
            focusRing,
          )}
        >
          {showAll ? "Show fewer" : `Show all ${total} forms`}
        </button>
      )}
    </div>
  );
}
